import React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/shared/ui/components/Button';
import { Input } from '@/shared/ui/components/Input';
import { Card } from '@/shared/ui/components/Card';
import { useLogin } from '@/entities/auth';
import { useAuthStore } from '@/shared/lib/store';
import type { LoginCredentials } from '@/entities/auth';
import { cn } from '@/shared/lib/utils';

interface LoginFormProps {
  className?: string;
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ 
  className,
  onSuccess 
}) => {
  const { setAuth, setLoading } = useAuthStore();
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    try {
      setLoading(true);
      
      const response = await loginMutation.mutateAsync({
        body: data,
      });

      if (response.success && response.data) {
        const { user, ...tokens } = response.data;
        setAuth(user, tokens);
        onSuccess?.();
      } else {
        setError('root', {
          message: response.error?.message || '로그인 실패',
        });
      }
    } catch (error: any) {
      setError('root', {
        message: error?.message || '로그인이 실패하였습니다. 아이디와 비밀번호를 확인해주세요.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={cn('w-full max-w-md mx-auto p-6 space-y-6', className)}>
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold text-gray-900">로그인</h1>
        <p className="text-sm text-gray-600">
          계정에 접속하려면 인증 정보를 입력하세요
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="username" className="text-sm font-medium text-gray-700">
            사용자명
          </label>
          <Input
            id="username"
            type="text"
            placeholder="사용자명을 입력하세요"
            {...register('username', {
              required: '사용자명은 필수입니다',
              minLength: {
                value: 3,
                message: '사용자명은 최소 3자 이상이어야 합니다',
              },
            })}
            error={errors.username?.message}
          />
          {errors.username && (
            <p className="text-sm text-danger">{errors.username.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-gray-700">
            비밀번호
          </label>
          <Input
            id="password"
            type="password"
            placeholder="비밀번호를 입력하세요"
            {...register('password', {
              required: '비밀번호는 필수입니다',
              minLength: {
                value: 6,
                message: '비밀번호는 최소 6자 이상이어야 합니다',
              },
            })}
            error={errors.password?.message}
          />
          {errors.password && (
            <p className="text-sm text-danger">{errors.password.message}</p>
          )}
        </div>

        {errors.root && (
          <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg">
            <p className="text-sm text-danger">{errors.root.message}</p>
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          loading={isSubmitting || loginMutation.isPending}
          disabled={isSubmitting || loginMutation.isPending}
        >
          {isSubmitting || loginMutation.isPending ? '로그인 중...' : '로그인'}
        </Button>
      </form>
    </Card>
  );
};

LoginForm.displayName = 'LoginForm';